import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box, Container, Typography, Paper, Button, TextField, Switch,
  FormControlLabel, Tab, Tabs, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Chip, InputAdornment, Avatar, Divider,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import StarIcon from '@mui/icons-material/Star'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import CampaignIcon from '@mui/icons-material/Campaign'
import TuneIcon from '@mui/icons-material/Tune'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import BarChartIcon from '@mui/icons-material/BarChart'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import {
  loyaltyConfig, loyaltyRedemptionTiersAdmin, loyaltyTierConfig,
  loyaltyCampaigns, loyaltyAnalytics, adminGetUserLoyalty,
  adminBlackCardSubscribers, blackCardProgram,
} from '../apollo/queries'
import {
  saveLoyaltyConfig, saveLoyaltyRedemptionTier, deleteLoyaltyRedemptionTier,
  saveLoyaltyTierConfig, saveLoyaltyCampaign, deleteLoyaltyCampaign,
  adminAdjustPoints, adminCancelBlackCard, saveBlackCardProgram,
} from '../apollo/mutations'

const GET_CONFIG = gql`${loyaltyConfig}`
const GET_TIERS = gql`${loyaltyRedemptionTiersAdmin}`
const GET_TIER_CONFIG = gql`${loyaltyTierConfig}`
const GET_CAMPAIGNS = gql`${loyaltyCampaigns}`
const GET_ANALYTICS = gql`${loyaltyAnalytics}`
const GET_USER_LOYALTY = gql`${adminGetUserLoyalty}`
const GET_BLACK_CARD_SUBS = gql`${adminBlackCardSubscribers}`
const GET_BLACK_CARD_PROGRAM = gql`${blackCardProgram}`

const SAVE_CONFIG = gql`${saveLoyaltyConfig}`
const SAVE_TIER = gql`${saveLoyaltyRedemptionTier}`
const DELETE_TIER = gql`${deleteLoyaltyRedemptionTier}`
const SAVE_TIER_CONFIG = gql`${saveLoyaltyTierConfig}`
const SAVE_CAMPAIGN = gql`${saveLoyaltyCampaign}`
const DELETE_CAMPAIGN = gql`${deleteLoyaltyCampaign}`
const ADJUST_POINTS = gql`${adminAdjustPoints}`
const CANCEL_BLACK_CARD = gql`${adminCancelBlackCard}`
const SAVE_BLACK_CARD_PROGRAM = gql`${saveBlackCardProgram}`

const TIER_META = {
  SILVER:   { color: '#64748b', bg: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)', light: '#f1f5f9', badge: '#94a3b8' },
  GOLD:     { color: '#b45309', bg: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)', light: '#fffbeb', badge: '#f59e0b' },
  PLATINUM: { color: '#6d28d9', bg: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)', light: '#f5f3ff', badge: '#8b5cf6' },
}

const TAB_ICONS = [TuneIcon, StarIcon, WorkspacePremiumIcon, CampaignIcon, ManageAccountsIcon, BarChartIcon, CreditCardIcon]
const TAB_LABELS = ['Earning Config', 'Redemption Tiers', 'Membership Tiers', 'Campaigns', 'Manual Adjustments', 'Analytics', 'Black Card']

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null
}

function SectionHeader({ title, subtitle, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
      <Box>
        <Typography variant="h6" fontWeight={700} color="#111827">{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
      {action}
    </Box>
  )
}

function StyledTable({ head, children, emptyMessage }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f9fafb' }}>
            {head.map(h => (
              <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
      </Table>
      {emptyMessage && (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
        </Box>
      )}
    </Paper>
  )
}

function StyledRow({ children }) {
  return (
    <TableRow sx={{ '&:hover': { bgcolor: '#f9fafb' }, '&:last-child td': { border: 0 } }}>
      {children}
    </TableRow>
  )
}

// ── Tab 1: Earning Config ─────────────────────────────────────────────────────
function EarningConfigTab() {
  const { data, loading } = useQuery(GET_CONFIG)
  const [saveConfig, { loading: saving }] = useMutation(SAVE_CONFIG, {
    refetchQueries: [{ query: GET_CONFIG }],
  })
  const [form, setForm] = useState(null)
  const [saved, setSaved] = useState(false)

  const cfg = data?.loyaltyConfig
  const current = form ?? cfg

  if (loading || !current) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>

  const handleSave = async () => {
    await saveConfig({
      variables: {
        input: {
          pointsPerDollar: parseFloat(current.pointsPerDollar),
          minOrderForRedemption: parseFloat(current.minOrderForRedemption),
          couponExpiryDays: parseInt(current.couponExpiryDays),
          allowStackWithPromo: current.allowStackWithPromo === true || current.allowStackWithPromo === 1,
          annualTierReset: current.annualTierReset === true || current.annualTierReset === 1,
        },
      },
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
    setForm(null)
  }

  const set = (key, val) => setForm(prev => ({ ...(prev ?? cfg), [key]: val }))

  return (
    <Box sx={{ maxWidth: 580 }}>
      <SectionHeader
        title="Earning Configuration"
        subtitle="Control how customers earn loyalty points on every order."
      />
      {saved && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          Configuration saved successfully
        </Alert>
      )}
      <Paper sx={{ borderRadius: 3, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {/* Points per dollar */}
        <Box sx={{ p: 3, borderBottom: '1px solid #f3f4f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#fef9c3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <StarIcon sx={{ fontSize: 18, color: '#ca8a04' }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={700} color="#111827">Points per $1 spent</Typography>
              <Typography variant="caption" color="text.secondary">Applied to food subtotal only (not tax/delivery)</Typography>
            </Box>
          </Box>
          <TextField
            type="number"
            fullWidth
            size="small"
            value={current?.pointsPerDollar ?? ''}
            onChange={e => set('pointsPerDollar', e.target.value)}
            inputProps={{ min: 0, step: 0.1 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              endAdornment: <InputAdornment position="end"><Typography variant="caption" color="text.secondary">pts / $1</Typography></InputAdornment>
            }}
          />
        </Box>

        {/* Min order */}
        <Box sx={{ p: 3, borderBottom: '1px solid #f3f4f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LocalOfferIcon sx={{ fontSize: 18, color: '#16a34a' }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={700} color="#111827">Minimum order to redeem</Typography>
              <Typography variant="caption" color="text.secondary">Minimum subtotal required to apply a coupon</Typography>
            </Box>
          </Box>
          <TextField
            type="number"
            fullWidth
            size="small"
            value={current?.minOrderForRedemption ?? ''}
            onChange={e => set('minOrderForRedemption', e.target.value)}
            inputProps={{ min: 0, step: 0.5 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
          />
        </Box>

        {/* Coupon expiry */}
        <Box sx={{ p: 3, borderBottom: '1px solid #f3f4f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#fdf4ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmojiEventsIcon sx={{ fontSize: 18, color: '#9333ea' }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={700} color="#111827">Coupon expiry</Typography>
              <Typography variant="caption" color="text.secondary">Days after redemption before the coupon expires</Typography>
            </Box>
          </Box>
          <TextField
            type="number"
            fullWidth
            size="small"
            value={current?.couponExpiryDays ?? ''}
            onChange={e => set('couponExpiryDays', e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              endAdornment: <InputAdornment position="end"><Typography variant="caption" color="text.secondary">days</Typography></InputAdornment>
            }}
          />
        </Box>

        {/* Toggles */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid #f3f4f6' }}>
            <Box>
              <Typography variant="body2" fontWeight={600} color="#111827">Stack with restaurant promotions</Typography>
              <Typography variant="caption" color="text.secondary">Allow loyalty coupon alongside a restaurant promo on the same order</Typography>
            </Box>
            <Switch
              checked={current?.allowStackWithPromo ?? true}
              onChange={e => set('allowStackWithPromo', e.target.checked)}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Box>
              <Typography variant="body2" fontWeight={600} color="#111827">Reset customer tiers annually</Typography>
              <Typography variant="caption" color="text.secondary">Resets each customer&apos;s membership tier at the start of the year</Typography>
            </Box>
            <Switch
              checked={current?.annualTierReset ?? false}
              onChange={e => set('annualTierReset', e.target.checked)}
            />
          </Box>
        </Box>
      </Paper>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !form}
          sx={{ borderRadius: 2, px: 3, fontWeight: 700, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  )
}

// ── Tab 2: Redemption Tiers ───────────────────────────────────────────────────
function RedemptionTiersTab() {
  const { data, loading, refetch } = useQuery(GET_TIERS)
  const [saveTier] = useMutation(SAVE_TIER, { onCompleted: () => { refetch(); setDialog(false) } })
  const [deleteTier] = useMutation(DELETE_TIER, { onCompleted: refetch })
  const [dialog, setDialog] = useState(false)
  const [editing, setEditing] = useState(null)

  const openNew = () => { setEditing({ pointsRequired: '', couponValue: '', isActive: true }); setDialog(true) }
  const openEdit = row => { setEditing({ ...row, id: row._id }); setDialog(true) }

  const handleSave = () => {
    saveTier({
      variables: {
        input: {
          id: editing.id || undefined,
          pointsRequired: parseInt(editing.pointsRequired),
          couponValue: parseFloat(editing.couponValue),
          isActive: editing.isActive,
        },
      },
    })
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>

  const tiers = data?.loyaltyRedemptionTiers ?? []

  return (
    <Box>
      <SectionHeader
        title="Redemption Tiers"
        subtitle="Define point milestones customers can redeem for discount coupons."
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
            sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
            Add Tier
          </Button>
        }
      />

      {tiers.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          {tiers.map(row => (
            <Paper key={row._id} sx={{ flex: '1 1 200px', maxWidth: 240, borderRadius: 3, border: '1px solid #e5e7eb', overflow: 'hidden', position: 'relative' }}>
              <Box sx={{ height: 4, background: row.isActive ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : '#e5e7eb' }} />
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: row.isActive ? '#fef9c3' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <StarIcon sx={{ fontSize: 20, color: row.isActive ? '#ca8a04' : '#9ca3af' }} />
                  </Box>
                  <Chip label={row.isActive ? 'Active' : 'Inactive'} size="small"
                    sx={{ bgcolor: row.isActive ? '#dcfce7' : '#fee2e2', color: row.isActive ? '#16a34a' : '#dc2626', fontWeight: 700, fontSize: '0.7rem' }} />
                </Box>
                <Typography variant="h5" fontWeight={800} color="#111827">
                  {row.pointsRequired.toLocaleString()}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>pts</Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>→</Typography>
                <Typography variant="h6" fontWeight={700} color="#16a34a">
                  ${parseFloat(row.couponValue).toFixed(2)} off
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: '#6b7280', '&:hover': { bgcolor: '#f3f4f6' } }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => deleteTier({ variables: { id: row._id } })}
                    sx={{ '&:hover': { bgcolor: '#fee2e2' } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ py: 6, textAlign: 'center', borderRadius: 3, border: '1.5px dashed #d1d5db' }}>
          <StarIcon sx={{ fontSize: 40, color: '#d1d5db', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">No redemption tiers yet. Add one to get started.</Typography>
        </Paper>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing?.id ? 'Edit Tier' : 'Add Redemption Tier'}</DialogTitle>
        <DialogContent>
          <TextField label="Points Required" type="number" fullWidth sx={{ mt: 1, mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            value={editing?.pointsRequired ?? ''} onChange={e => setEditing(p => ({ ...p, pointsRequired: e.target.value }))} />
          <TextField label="Coupon Value ($)" type="number" fullWidth sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            value={editing?.couponValue ?? ''} onChange={e => setEditing(p => ({ ...p, couponValue: e.target.value }))} />
          <FormControlLabel control={<Switch checked={editing?.isActive ?? true}
            onChange={e => setEditing(p => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialog(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
            Save Tier
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ── Tab 3: Membership Tiers ───────────────────────────────────────────────────
function MembershipTiersTab() {
  const { data, loading, refetch } = useQuery(GET_TIER_CONFIG)
  const [saveTierCfg] = useMutation(SAVE_TIER_CONFIG, { onCompleted: () => { refetch(); setEditing(null) } })
  const [editing, setEditing] = useState(null)

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>

  const handleSave = () => {
    saveTierCfg({
      variables: {
        input: {
          id: editing._id,
          tierName: editing.tierName,
          minLifetimePoints: parseInt(editing.minLifetimePoints),
          maxLifetimePoints: editing.maxLifetimePoints ? parseInt(editing.maxLifetimePoints) : null,
          earningMultiplier: parseFloat(editing.earningMultiplier),
        },
      },
    })
  }

  const tiers = data?.loyaltyTierConfig ?? []

  return (
    <Box>
      <SectionHeader
        title="Membership Tiers"
        subtitle="Tiers are based on lifetime points earned. Redeeming points does not lower a customer's tier."
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        {tiers.map(row => {
          const meta = TIER_META[row.tierKey] || TIER_META.SILVER
          return (
            <Paper key={row._id} sx={{ flex: '1 1 220px', maxWidth: 260, borderRadius: 3, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              <Box sx={{ background: meta.bg, p: 2.5, color: '#fff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="overline" sx={{ opacity: 0.8, fontSize: '0.68rem' }}>Tier</Typography>
                    <Typography variant="h6" fontWeight={800}>{row.tierName}</Typography>
                  </Box>
                  <WorkspacePremiumIcon sx={{ fontSize: 32, opacity: 0.85 }} />
                </Box>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 1 }}>{row.earningMultiplier}x</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>earning multiplier</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">Min lifetime pts</Typography>
                  <Typography variant="caption" fontWeight={700}>{row.minLifetimePoints.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Max lifetime pts</Typography>
                  <Typography variant="caption" fontWeight={700}>{row.maxLifetimePoints != null ? row.maxLifetimePoints.toLocaleString() : '∞'}</Typography>
                </Box>
                <Button
                  fullWidth
                  size="small"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={() => setEditing(row)}
                  variant="outlined"
                  sx={{ borderRadius: 1.5, fontWeight: 600, borderColor: '#e5e7eb', color: '#374151', '&:hover': { bgcolor: '#f9fafb', borderColor: '#d1d5db' } }}
                >
                  Edit
                </Button>
              </Box>
            </Paper>
          )
        })}
      </Box>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit {editing?.tierName} Tier</DialogTitle>
        <DialogContent>
          <TextField label="Tier Name" fullWidth sx={{ mt: 1, mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            value={editing?.tierName ?? ''} onChange={e => setEditing(p => ({ ...p, tierName: e.target.value }))} />
          <TextField label="Min Lifetime Points" type="number" fullWidth sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            value={editing?.minLifetimePoints ?? ''} onChange={e => setEditing(p => ({ ...p, minLifetimePoints: e.target.value }))} />
          <TextField label="Max Lifetime Points (blank = no max)" type="number" fullWidth sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            value={editing?.maxLifetimePoints ?? ''} onChange={e => setEditing(p => ({ ...p, maxLifetimePoints: e.target.value }))} />
          <TextField label="Earning Multiplier (e.g. 1.25)" type="number" fullWidth sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            inputProps={{ step: 0.05, min: 1 }}
            value={editing?.earningMultiplier ?? ''} onChange={e => setEditing(p => ({ ...p, earningMultiplier: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditing(null)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ── Tab 4: Campaigns ──────────────────────────────────────────────────────────
function CampaignsTab() {
  const { data, loading, refetch } = useQuery(GET_CAMPAIGNS)
  const [saveCampaign] = useMutation(SAVE_CAMPAIGN, { onCompleted: () => { refetch(); setDialog(false) } })
  const [deleteCampaign] = useMutation(DELETE_CAMPAIGN, { onCompleted: refetch })
  const [dialog, setDialog] = useState(false)
  const [editing, setEditing] = useState(null)

  const now = new Date()
  const getStatus = c => {
    if (!c.isActive) return { label: 'Inactive', bg: '#f1f5f9', color: '#64748b' }
    if (new Date(c.startsAt) > now) return { label: 'Upcoming', bg: '#fef9c3', color: '#ca8a04' }
    if (new Date(c.endsAt) < now) return { label: 'Ended', bg: '#fee2e2', color: '#dc2626' }
    return { label: 'Active', bg: '#dcfce7', color: '#16a34a' }
  }

  const openNew = () => {
    const d = new Date()
    setEditing({ name: '', multiplier: 2, startsAt: d.toISOString().slice(0, 16), endsAt: d.toISOString().slice(0, 16), isActive: true })
    setDialog(true)
  }

  const handleSave = () => {
    saveCampaign({
      variables: {
        input: {
          id: editing.id || undefined,
          name: editing.name,
          multiplier: parseFloat(editing.multiplier),
          startsAt: new Date(editing.startsAt).toISOString(),
          endsAt: new Date(editing.endsAt).toISOString(),
          isActive: editing.isActive,
        },
      },
    })
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>

  const campaigns = data?.loyaltyCampaigns ?? []

  return (
    <Box>
      <SectionHeader
        title="Bonus Point Campaigns"
        subtitle="Run time-limited events that multiply points earned during the campaign window."
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
            sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
            New Campaign
          </Button>
        }
      />

      <StyledTable
        head={['Campaign', 'Multiplier', 'Period', 'Status', '']}
        emptyMessage={campaigns.length === 0 ? 'No campaigns yet. Create one to reward customers during special events.' : null}
      >
        {campaigns.map(row => {
          const s = getStatus(row)
          return (
            <StyledRow key={row._id}>
              <TableCell sx={{ fontWeight: 600, color: '#111827' }}>{row.name}</TableCell>
              <TableCell>
                <Chip label={`${row.multiplier}x`} size="small"
                  sx={{ bgcolor: '#fef9c3', color: '#92400e', fontWeight: 800, fontSize: '0.8rem' }} />
              </TableCell>
              <TableCell sx={{ color: '#6b7280', fontSize: '0.82rem' }}>
                {new Date(row.startsAt).toLocaleDateString()} → {new Date(row.endsAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.72rem' }} />
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => { setEditing({ ...row, id: row._id, startsAt: row.startsAt.slice(0, 16), endsAt: row.endsAt.slice(0, 16) }); setDialog(true) }}
                  sx={{ color: '#6b7280', mr: 0.5 }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => deleteCampaign({ variables: { id: row._id } })}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </StyledRow>
          )
        })}
      </StyledTable>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing?.id ? 'Edit Campaign' : 'New Campaign'}</DialogTitle>
        <DialogContent>
          <TextField label="Campaign Name" fullWidth sx={{ mt: 1, mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            value={editing?.name ?? ''} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} />
          <TextField label="Point Multiplier (e.g. 2 = double points)" type="number" fullWidth sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            inputProps={{ step: 0.5, min: 1 }}
            value={editing?.multiplier ?? ''} onChange={e => setEditing(p => ({ ...p, multiplier: e.target.value }))} />
          <TextField label="Start Date & Time" type="datetime-local" fullWidth sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputLabelProps={{ shrink: true }}
            value={editing?.startsAt ?? ''} onChange={e => setEditing(p => ({ ...p, startsAt: e.target.value }))} />
          <TextField label="End Date & Time" type="datetime-local" fullWidth sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputLabelProps={{ shrink: true }}
            value={editing?.endsAt ?? ''} onChange={e => setEditing(p => ({ ...p, endsAt: e.target.value }))} />
          <FormControlLabel control={<Switch checked={editing?.isActive ?? true}
            onChange={e => setEditing(p => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialog(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ── Tab 5: Manual Adjustments ─────────────────────────────────────────────────
function ManualAdjustmentsTab() {
  const [userId, setUserId] = useState('')
  const [searched, setSearched] = useState(null)
  const [points, setPoints] = useState('')
  const [reason, setReason] = useState('')
  const [success, setSuccess] = useState('')

  const { loading: fetching } = useQuery(GET_USER_LOYALTY, {
    variables: { userId: searched || '0' },
    skip: !searched,
    onCompleted: data => setUserData(data?.adminGetUserLoyalty),
  })
  const [userData, setUserData] = useState(null)
  const [adjustPoints, { loading: adjusting }] = useMutation(ADJUST_POINTS, {
    onCompleted: data => {
      setUserData(data.adminAdjustPoints)
      setPoints('')
      setReason('')
      setSuccess('Points adjusted successfully')
      setTimeout(() => setSuccess(''), 3000)
    },
  })

  const handleSearch = () => { setSearched(userId); setUserData(null) }
  const handleAdjust = () => {
    if (!points || !reason) return
    adjustPoints({ variables: { userId: searched, points: parseInt(points), reason } })
  }

  const ptsNum = parseInt(points) || 0

  return (
    <Box sx={{ maxWidth: 640 }}>
      <SectionHeader
        title="Manual Point Adjustments"
        subtitle="Look up a customer and manually add or deduct loyalty points."
      />

      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', mb: 3 }}>
        <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 1.5 }}>Customer Lookup</Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            label="Customer User ID"
            fullWidth
            size="small"
            value={userId}
            onChange={e => setUserId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Button
            variant="contained"
            startIcon={fetching ? <CircularProgress size={14} color="inherit" /> : <PersonSearchIcon />}
            onClick={handleSearch}
            disabled={fetching || !userId}
            sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' }, whiteSpace: 'nowrap', px: 2.5 }}
          >
            Lookup
          </Button>
        </Box>
      </Paper>

      {userData && (
        <Paper sx={{ borderRadius: 3, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {/* Customer stats */}
          <Box sx={{ p: 3, borderBottom: '1px solid #f3f4f6' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'Current Balance', value: userData.balance.toLocaleString() + ' pts', color: '#111827' },
                { label: 'Lifetime Earned', value: userData.lifetimePoints.toLocaleString() + ' pts', color: '#111827' },
              ].map(s => (
                <Box key={s.label} sx={{ flex: '1 1 140px', p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  <Typography variant="h6" fontWeight={800} color={s.color}>{s.value}</Typography>
                </Box>
              ))}
              <Box sx={{ flex: '1 1 100px', p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Tier</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={userData.tier} size="small"
                    sx={{
                      bgcolor: (TIER_META[userData.tier]?.badge || '#94a3b8') + '22',
                      color: TIER_META[userData.tier]?.color || '#64748b',
                      fontWeight: 700,
                    }} />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Adjustment form */}
          <Box sx={{ p: 3, borderBottom: '1px solid #f3f4f6' }}>
            <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 2 }}>Apply Adjustment</Typography>
            {success && <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
              <TextField
                label="Points (negative to deduct)"
                type="number"
                size="small"
                sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                value={points}
                onChange={e => setPoints(e.target.value)}
                InputProps={{
                  startAdornment: ptsNum > 0 ? (
                    <InputAdornment position="start"><Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700 }}>+</Typography></InputAdornment>
                  ) : ptsNum < 0 ? (
                    <InputAdornment position="start"><Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 700 }}>−</Typography></InputAdornment>
                  ) : null,
                }}
              />
              <TextField
                label="Reason (required)"
                size="small"
                sx={{ flex: 1, minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                value={reason}
                onChange={e => setReason(e.target.value)}
              />
            </Box>
            <Button
              variant="contained"
              onClick={handleAdjust}
              disabled={adjusting || !points || !reason}
              sx={{ borderRadius: 2, fontWeight: 700, bgcolor: ptsNum < 0 ? '#dc2626' : '#111827', '&:hover': { bgcolor: ptsNum < 0 ? '#b91c1c' : '#1f2937' } }}
            >
              {adjusting ? 'Applying...' : ptsNum < 0 ? 'Deduct Points' : 'Add Points'}
            </Button>
          </Box>

          {/* Transaction history */}
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" fontWeight={700} color="#374151" sx={{ mb: 2 }}>Recent Transactions</Typography>
            <StyledTable head={['Type', 'Points', 'Status', 'Description', 'Date']}>
              {(userData.transactions ?? []).slice(0, 10).map(tx => (
                <StyledRow key={tx._id}>
                  <TableCell sx={{ fontSize: '0.82rem', color: '#374151' }}>{tx.type}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: tx.points > 0 ? '#16a34a' : '#dc2626' }}>
                    {tx.points > 0 ? '+' : ''}{tx.points}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.82rem' }}>{tx.status}</TableCell>
                  <TableCell sx={{ fontSize: '0.82rem', color: '#6b7280', maxWidth: 200 }}>{tx.description}</TableCell>
                  <TableCell sx={{ fontSize: '0.82rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : '—'}
                  </TableCell>
                </StyledRow>
              ))}
            </StyledTable>
          </Box>
        </Paper>
      )}
    </Box>
  )
}

// ── Tab 6: Analytics ──────────────────────────────────────────────────────────
function AnalyticsTab() {
  const { data, loading } = useQuery(GET_ANALYTICS)
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
  const a = data?.loyaltyAnalytics
  if (!a) return null

  const statCards = [
    { label: 'Total Points Issued', value: a.totalPointsIssued.toLocaleString(), icon: TrendingUpIcon, iconBg: '#eff6ff', iconColor: '#2563eb' },
    { label: 'Total Points Redeemed', value: a.totalPointsRedeemed.toLocaleString(), icon: StarIcon, iconBg: '#fef9c3', iconColor: '#ca8a04' },
    { label: 'Coupon Value Issued', value: `$${parseFloat(a.totalCouponValueIssued).toFixed(2)}`, icon: LocalOfferIcon, iconBg: '#f0fdf4', iconColor: '#16a34a' },
    { label: 'Active Coupons', value: a.activeCouponsCount.toLocaleString(), icon: EmojiEventsIcon, iconBg: '#fdf4ff', iconColor: '#9333ea' },
  ]

  return (
    <Box>
      <SectionHeader
        title="Analytics"
        subtitle="Overview of your loyalty program performance."
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        {statCards.map(c => {
          const Icon = c.icon
          return (
            <Paper key={c.label} sx={{ flex: '1 1 180px', p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: c.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon sx={{ fontSize: 20, color: c.iconColor }} />
                </Box>
              </Box>
              <Typography variant="h5" fontWeight={800} color="#111827">{c.value}</Typography>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
            </Paper>
          )
        })}
      </Box>

      <SectionHeader title="Top Customers by Lifetime Points" />
      <StyledTable head={['#', 'Name', 'Email', 'Lifetime Points', 'Tier']}>
        {(a.topCustomers ?? []).map((c, i) => (
          <StyledRow key={c.userId}>
            <TableCell sx={{ fontWeight: 700, color: '#9ca3af', width: 40 }}>{i + 1}</TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.78rem', bgcolor: '#f3f4f6', color: '#374151', fontWeight: 700 }}>
                  {(c.name || '?')[0].toUpperCase()}
                </Avatar>
                <Typography variant="body2" fontWeight={600} color="#111827">{c.name || '—'}</Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ color: '#6b7280', fontSize: '0.85rem' }}>{c.email || '—'}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{c.lifetimePoints.toLocaleString()}</TableCell>
            <TableCell>
              <Chip label={c.tier} size="small"
                sx={{ bgcolor: (TIER_META[c.tier]?.badge || '#94a3b8') + '22', color: TIER_META[c.tier]?.color || '#64748b', fontWeight: 700, fontSize: '0.72rem' }} />
            </TableCell>
          </StyledRow>
        ))}
      </StyledTable>
    </Box>
  )
}

// ── Tab 7: Black Card ─────────────────────────────────────────────────────────
// The super admin's program settings (utils/blackCardProgram.js on the backend).
function BlackCardProgramSettings() {
  const { data, loading } = useQuery(GET_BLACK_CARD_PROGRAM, { fetchPolicy: 'network-only' })
  const [saveProgram, { loading: saving }] = useMutation(SAVE_BLACK_CARD_PROGRAM, {
    refetchQueries: [{ query: GET_BLACK_CARD_PROGRAM }],
  })
  const [form, setForm] = useState(null)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  const program = data?.blackCardProgram
  const current = form ?? program

  if (loading || !current) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>

  const set = (key, val) => setForm(prev => ({ ...(prev ?? program), [key]: val }))

  const handleSave = async () => {
    setError(null)
    try {
      await saveProgram({
        variables: {
          input: {
            enabled: !!current.enabled,
            monthlyPrice: parseFloat(current.monthlyPrice),
            deliveryDiscountCap: parseFloat(current.deliveryDiscountCap),
            bonusPointsPerOrder: parseInt(current.bonusPointsPerOrder),
          },
        },
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      setForm(null)
    } catch (e) {
      setError(e.message)
    }
  }

  const rows = [
    {
      key: 'monthlyPrice', icon: CreditCardIcon, tint: '#fef9c3', color: '#ca8a04',
      title: 'Monthly price',
      caption: 'What new members pay each month. Current members keep the price they joined at.',
      step: 0.01, min: 0.5, start: '$',
    },
    {
      key: 'deliveryDiscountCap', icon: LocalShippingIcon, tint: '#f0fdf4', color: '#16a34a',
      title: 'Delivery fee covered per order',
      caption: 'The platform pays up to this much of a member\'s delivery fee; anything above it is charged. 0 turns this perk off.',
      step: 0.5, min: 0, start: '$',
    },
    {
      key: 'bonusPointsPerOrder', icon: StarIcon, tint: '#fdf4ff', color: '#9333ea',
      title: 'Bonus loyalty points per order',
      caption: 'Added on top of regular points on every member order. 0 turns this perk off.',
      step: 1, min: 0, end: 'pts',
    },
  ]

  return (
    <Box sx={{ maxWidth: 580, mb: 4 }}>
      <SectionHeader
        title="Black Card Program"
        subtitle="Turn Black Card on or off and set its price and perks. Changes apply right away."
      />
      {saved && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          Black Card settings saved
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
      <Paper sx={{ borderRadius: 3, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="body2" fontWeight={700} color="#111827">
              Black Card is {current.enabled ? 'on' : 'off'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {current.enabled
                ? 'Customers can see Black Card and become members.'
                : 'Hidden from customers who aren\'t members, and no one new can join. Current members keep their benefits and renewals until they cancel.'}
            </Typography>
          </Box>
          <Switch checked={!!current.enabled} onChange={e => set('enabled', e.target.checked)} />
        </Box>

        {rows.map((r, i) => {
          const RowIcon = r.icon
          return (
            <Box key={r.key} sx={{ p: 3, borderBottom: i < rows.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: r.tint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <RowIcon sx={{ fontSize: 18, color: r.color }} />
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={700} color="#111827">{r.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.caption}</Typography>
                </Box>
              </Box>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={current?.[r.key] ?? ''}
                onChange={e => set(r.key, e.target.value)}
                inputProps={{ min: r.min, step: r.step }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{
                  ...(r.start ? { startAdornment: <InputAdornment position="start">{r.start}</InputAdornment> } : {}),
                  ...(r.end ? { endAdornment: <InputAdornment position="end"><Typography variant="caption" color="text.secondary">{r.end}</Typography></InputAdornment> } : {}),
                }}
              />
            </Box>
          )
        })}
      </Paper>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !form}
          sx={{ borderRadius: 2, px: 3, fontWeight: 700, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  )
}

function BlackCardTab() {
  const { data, loading, refetch } = useQuery(GET_BLACK_CARD_SUBS, { fetchPolicy: 'network-only' })
  const { data: programData } = useQuery(GET_BLACK_CARD_PROGRAM)
  const [cancelBlackCard] = useMutation(CANCEL_BLACK_CARD, {
    onCompleted: () => refetch(),
  })
  const subs = data?.adminBlackCardSubscribers ?? []
  const activeSubs = subs.filter(s => s.status === 'ACTIVE')
  // Each member pays the price they joined at; memberships from before that was
  // kept show today's price.
  const priceOf = (sub) => sub.monthlyPrice ?? programData?.blackCardProgram?.monthlyPrice ?? 0
  const monthlyRevenue = activeSubs.reduce((sum, s) => sum + priceOf(s), 0)

  const statusColor = (status) => {
    if (status === 'ACTIVE') return { bg: '#dcfce7', color: '#15803d' }
    if (status === 'PAST_DUE') return { bg: '#fff7ed', color: '#c2410c' }
    return { bg: '#f1f5f9', color: '#475569' }
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>

  return (
    <Box>
      <BlackCardProgramSettings />

      <Paper sx={{ p: 3, mb: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="body2" color="text.secondary">Active Subscribers</Typography>
          <Typography variant="h4" fontWeight={700}>{activeSubs.length}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">Monthly Revenue</Typography>
          <Typography variant="h4" fontWeight={700}>${monthlyRevenue.toFixed(2)}</Typography>
        </Box>
      </Paper>

      <Table>
        <TableHead>
          <TableRow sx={{ background: '#f8fafc' }}>
            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Renewal Date</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subs.length === 0 && (
            <TableRow><TableCell colSpan={6} align="center" sx={{ color: '#94a3b8', py: 4 }}>No Black Card subscribers yet</TableCell></TableRow>
          )}
          {subs.map(sub => {
            const sc = statusColor(sub.status)
            return (
              <TableRow key={sub._id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{sub.userName || '—'}</TableCell>
                <TableCell sx={{ color: '#6b7280' }}>{sub.userEmail || '—'}</TableCell>
                <TableCell>
                  <Chip label={sub.status} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 700, fontSize: '0.72rem' }} />
                </TableCell>
                <TableCell>${priceOf(sub).toFixed(2)}/mo</TableCell>
                <TableCell>{sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString() : '—'}</TableCell>
                <TableCell>
                  {sub.status === 'ACTIVE' && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => {
                        if (window.confirm(`Cancel Black Card for ${sub.userName || sub.userEmail}?`)) {
                          cancelBlackCard({ variables: { userId: sub.userId } })
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
function LoyaltyProgram() {
  const [tab, setTab] = useState(0)
  const Icon = TAB_ICONS[tab]

  return (
    <Box sx={{ background: '#f8fafc', minHeight: '100vh' }}>
      {/* Hero header */}
      <Box sx={{ background: 'linear-gradient(135deg, #111827 0%, #1f2937 60%, #374151 100%)', px: { xs: 3, md: 4 }, py: 4 }}>
        <Container maxWidth="lg" disableGutters>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmojiEventsIcon sx={{ fontSize: 26, color: '#fbbf24' }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800} color="#fff" sx={{ lineHeight: 1.1 }}>
                Loyalty Program
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.25 }}>
                Configure the points-based loyalty program. All settings take effect immediately.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Tab bar */}
        <Paper sx={{ borderRadius: 2.5, border: '1px solid #e5e7eb', mb: 3, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': { fontWeight: 600, fontSize: '0.85rem', textTransform: 'none', minHeight: 52, gap: 0.75, color: '#6b7280' },
              '& .Mui-selected': { color: '#111827', fontWeight: 700 },
              '& .MuiTabs-indicator': { bgcolor: '#111827', height: 3, borderRadius: '3px 3px 0 0' },
              px: 1,
            }}
          >
            {TAB_LABELS.map((label, i) => {
              const TabIcon = TAB_ICONS[i]
              return (
                <Tab
                  key={label}
                  label={label}
                  icon={<TabIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                />
              )
            })}
          </Tabs>
        </Paper>

        <TabPanel value={tab} index={0}><EarningConfigTab /></TabPanel>
        <TabPanel value={tab} index={1}><RedemptionTiersTab /></TabPanel>
        <TabPanel value={tab} index={2}><MembershipTiersTab /></TabPanel>
        <TabPanel value={tab} index={3}><CampaignsTab /></TabPanel>
        <TabPanel value={tab} index={4}><ManualAdjustmentsTab /></TabPanel>
        <TabPanel value={tab} index={5}><AnalyticsTab /></TabPanel>
        <TabPanel value={tab} index={6}><BlackCardTab /></TabPanel>
      </Container>
    </Box>
  )
}

export default LoyaltyProgram
